import { useUserAccount } from '@/api/user/queries';
import { GenericError } from '@/models/generic-error';
import { PaginatedResponse } from '@/models/pagination/paginated-response.dto';
import { DetailedUserReward } from '@/models/user-reward/detailed';
import { RewardStatusId } from '@/models/user-reward/reward/status/id';
import { UserRewardsHistoryQuery } from '@/services/dto/user-rewards/user-rewards-history.query.dto';
import { usePaginatedQuery } from '@starsoft/common/hooks';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { UserRewardsQueryKeys } from '../../queryKeys';
import {
  UserRewardsHistoryQueryKey,
  UseUserRewardsHistoryProps,
} from './props';
import { fetchUserRewardsHistory } from './service';

export function useUserRewardsHistory(options?: UseUserRewardsHistoryProps) {
  const { statusId = RewardStatusId.Active.toString(), refetchOnMount = true } =
    options ?? {};
  const [page, setPage] = useState<number>(1);
  const { isAuthorized } = useUserAccount();

  const filter: UserRewardsHistoryQuery = {
    limit: 20,
    page,
    statusId,
  };

  const queryKey: UserRewardsHistoryQueryKey = [
    UserRewardsQueryKeys.History,
    filter,
  ];

  const { data, refetch, isPending, error, fetchStatus, ...rest } = useQuery<
    PaginatedResponse<DetailedUserReward>,
    AxiosError<GenericError>,
    PaginatedResponse<DetailedUserReward>,
    UserRewardsHistoryQueryKey
  >({
    queryKey,
    queryFn: fetchUserRewardsHistory,
    enabled: isAuthorized,
    staleTime: 5 * 1000,
    refetchOnMount,
  });

  const paginatedCallbacks = usePaginatedQuery({
    page,
    setPage,
    data,
    queryParams: {
      statusId,
    },
  });

  return {
    error,
    refetch,
    isLoading: isPending && fetchStatus !== 'idle',
    data,
    ...paginatedCallbacks,
  };
}
